import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Button, Card, Grid, Tooltip, Typography } from '@frontierag/frontier-ui-library';
import {
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Warning as WarningIcon,
} from '@frontierag/frontier-ui-library/icons';
import { generateTestId } from 'utils';

import { styles } from './styles';
import type { IProductCard } from './types';

const BasketProductCard = ({
  productId,
  actions,
  fields,
  title,
  hasError,
  errorTooltip,
  displayVertical = false,
  withViewMore = false,
}: IProductCard) => {
  const [displayHidden, setDisplayHidden] = useState(false);
  const { t } = useTranslation('common');

  const onViewMoreClick = useCallback(() => {
    setDisplayHidden(!displayHidden);
  }, [displayHidden]);

  return (
    <Card
      variant="outlined"
      sx={styles.cardWrapper}
      data-testid={`basket-product-card-${productId}`}
    >
      <Grid container direction="column" spacing={1} data-testid={`basket-product-card-content`}>
        <Grid item mb={0.5} display="flex" justifyContent="space-between">
          <Typography variant="subtitle3">{title}</Typography>
          {hasError &&
            (errorTooltip ? (
              <Tooltip
                title={errorTooltip}
                componentsProps={{
                  tooltip: {
                    sx: styles.tooltip,
                  },
                }}
              >
                <Box component="span" tabIndex={0} display="inline-flex">
                  <WarningIcon color="warning" fontSize="small" />
                </Box>
              </Tooltip>
            ) : (
              <WarningIcon color="warning" fontSize="small" />
            ))}
        </Grid>
        {fields
          .filter((field) => !withViewMore || !field.isHidden || displayHidden)
          .map(({ label, value }) => (
            <Grid
              container
              item
              key={`field-${generateTestId(label)}-${productId}`}
              data-testid={`field-${generateTestId(label)}-${productId}`}
              alignItems="center"
            >
              <Grid item xs={displayVertical ? 12 : 5} display="flex" alignItems="flex-start">
                <Typography variant="label2" color="grey.L20" sx={{ pt: 2 / 8 }}>
                  {label}:
                </Typography>
              </Grid>
              <Grid item xs={displayVertical ? 12 : 7} display="flex" alignItems="flex-start">
                <Typography variant="label1">{value}</Typography>
              </Grid>
            </Grid>
          ))}
      </Grid>
      <Box mt={3}>
        {withViewMore && (
          <Box>
            <Button
              variant="text"
              endIcon={displayHidden ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              onClick={onViewMoreClick}
              color="primary"
              fullWidth
              size="small"
              data-testid={`view-more-product-${productId}`}
            >
              {t(`buttons.${displayHidden ? 'viewLess' : 'viewMore'}`)}
            </Button>
          </Box>
        )}
        <Box display="flex" justifyContent="flex-end" gap={1} mt={1.5}>
          {actions.map((action, index) => (
            <Button
              key={index}
              variant="contained"
              startIcon={action.icon}
              onClick={() => action.onActionClick(productId)}
              disabled={action.disabled}
              color="secondary"
              sx={{ width: '50%' }}
              size="small"
              data-testid={`action-${action.title.toLowerCase()}-product-${productId}`}
              title={action.title}
            >
              {action.title}
            </Button>
          ))}
        </Box>
      </Box>
    </Card>
  );
};
export default BasketProductCard;
